import {
  AnalyticIdentifier,
  CapitalizationChangeReason,
  dateToMonthYear,
  getFirstDateOfMonthYear,
  getLastDateOfMonthYear,
  getMonthShortAbbreviation,
  toIsoDateString,
} from '@common/neo.tax';
import { Button, colors, ControlledToggleSwitch, ModalHeader, Stack, Typography } from '@ui-components/neo.tax';
import { ReactComponent as Presentation } from '@ui-components/neo.tax/dist/icons/presentation-projector-screen-hierarchy.svg';
import { ReactElement, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useAscContext } from '@neo.tax/asc/context';
import { type AscProject, useAscProjectApprovalStatus, useUpdateAscProjectCapitalization } from '@neo.tax/asc/hooks';
import { ControlledMonthYearPicker, defaultMonthOptions, useAnalyticId, useCurrentCompany } from '@neo.tax/shared';

import { calculateEarliestAffectedMonth } from '../asc-project-detail-form/asc-project-detail-form.utils';
import { AscProjectDetailCapitalizationEndDateLabel } from '../asc-project-detail-form/AscProjectDetailCapitalizationEndDateLabel';
import { Modal, StyledAlert, StyledDivider, StyledModalActions } from './capitalization-edit-modal.styled';
import { CapitalizationEditForm } from './capitalization-edit-modal.types';
import { useCapitalizationFieldConfig } from './useCapitalizationFieldConfig';

interface CapitalizationEditModalContentProps {
  isOpen: boolean;
  onClose: () => void;
  ascProject: AscProject;
}

const makeDefaultCapitalizationFormValues = (ascProject: AscProject): CapitalizationEditForm => ({
  isCapitalized: ascProject.isCapitalized,
  capitalizationStartMonthYear: ascProject.capitalizationStartDate
    ? dateToMonthYear(ascProject.capitalizationStartDate)
    : null,
  capitalizationEndMonthYear: ascProject.capitalizationEndDate
    ? dateToMonthYear(ascProject.capitalizationEndDate)
    : null,
  predictedCapitalizationStartMonthYear: ascProject.predictedCapitalizationStartDate
    ? dateToMonthYear(ascProject.predictedCapitalizationStartDate)
    : null,
  predictedCapitalizationEndMonthYear: ascProject.predictedCapitalizationEndDate
    ? dateToMonthYear(ascProject.predictedCapitalizationEndDate)
    : null,
});

/**
 * Modal content for editing ASC project capitalization settings.
 * Use CapitalizationEditModal if you only have the ascProjectId.
 */
export const CapitalizationEditModalContent = ({
  ascProject,
  isOpen,
  onClose,
}: CapitalizationEditModalContentProps): ReactElement => {
  const { fiscalYearId, periodName } = useAscContext();
  const { company } = useCurrentCompany();
  const [updateCapitalization, { loading }] = useUpdateAscProjectCapitalization({
    ascProject,
    fiscalYearId,
    periodName,
  });

  // Query the approval status when modal opens
  const { approvalStatus } = useAscProjectApprovalStatus(ascProject.id, { skip: !isOpen });

  const approvedThroughMonthYear = approvalStatus?.approvedThroughMonthYear ?? null;

  // Get lock boundary from company
  const lockBoundary = company?.ascLockedUntilMonthYear ?? null;

  const capitalizationStatusSwitchId = useAnalyticId(AnalyticIdentifier.ASC_PROJECT_DETAIL_CAPITALIZATION_STATUS);
  const capitalizationStartDateId = useAnalyticId(AnalyticIdentifier.ASC_PROJECT_DETAIL_CAPITALIZATION_START_DATE);
  const capitalizationEndDateId = useAnalyticId(AnalyticIdentifier.ASC_PROJECT_DETAIL_CAPITALIZATION_END_DATE);

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm<CapitalizationEditForm>({
    mode: 'onChange',
    defaultValues: makeDefaultCapitalizationFormValues(ascProject),
  });

  const isCapitalized = watch('isCapitalized');
  const capitalizationStartMonthYear = watch('capitalizationStartMonthYear');
  const capitalizationEndMonthYear = watch('capitalizationEndMonthYear');
  const predictedCapitalizationEndMonthYear = watch('predictedCapitalizationEndMonthYear');

  // Get field configurations (lock states, disabled, clearable, min/max, helpText, validate)
  const fieldConfig = useCapitalizationFieldConfig({
    ascProject,
    lockBoundary,
    capitalizationStartMonthYear,
    capitalizationEndMonthYear,
  });

  // Calculate the old (saved) values for comparison
  const oldStartMonthYear = ascProject.capitalizationStartDate
    ? dateToMonthYear(ascProject.capitalizationStartDate)
    : null;
  const oldEndMonthYear = ascProject.capitalizationEndDate ? dateToMonthYear(ascProject.capitalizationEndDate) : null;

  // Calculate new values based on whether capitalization is enabled
  const newStartMonthYear = isCapitalized ? capitalizationStartMonthYear : null;
  const newEndMonthYear = isCapitalized ? capitalizationEndMonthYear : null;

  // Calculate the earliest month that would be affected by this change
  const earliestAffectedResult = useMemo(
    () =>
      calculateEarliestAffectedMonth(
        oldStartMonthYear,
        oldEndMonthYear,
        newStartMonthYear,
        newEndMonthYear,
        approvedThroughMonthYear,
      ),
    [oldStartMonthYear, oldEndMonthYear, newStartMonthYear, newEndMonthYear, approvedThroughMonthYear],
  );

  // Reason messages for the warning
  const reasonMessages: Record<CapitalizationChangeReason, string> = {
    [CapitalizationChangeReason.MONTHS_NOW_CAPITALIZED]: 'because these months will now be capitalized',
    [CapitalizationChangeReason.MONTHS_NO_LONGER_CAPITALIZED]: 'because these months will no longer be capitalized',
    [CapitalizationChangeReason.CAPITALIZATION_PERIOD_CHANGED]: 'because the capitalization period changed',
  };

  // Format the warning message
  const approvalWarningMessage = earliestAffectedResult
    ? `This will reset approval from ${getMonthShortAbbreviation(earliestAffectedResult.monthYear.month)} ${
        earliestAffectedResult.monthYear.year
      } onward. Re-approval will be required ${reasonMessages[earliestAffectedResult.reason]}.`
    : null;

  // Reset form when modal opens with latest ascProject data
  useEffect(() => {
    if (isOpen) {
      reset(makeDefaultCapitalizationFormValues(ascProject));
    }
  }, [isOpen, ascProject, reset]);

  const handleCancel = (): void => {
    reset(makeDefaultCapitalizationFormValues(ascProject));
    onClose();
  };

  const handleToggleChange = (checked: boolean): void => {
    setValue('isCapitalized', checked, { shouldDirty: true });

    // When enabling capitalization, prefill with predicted dates if available
    if (checked && !ascProject.capitalizationStartDate) {
      const startMonthYear = ascProject.predictedCapitalizationStartDate
        ? dateToMonthYear(ascProject.predictedCapitalizationStartDate)
        : null;
      setValue('capitalizationStartMonthYear', startMonthYear, { shouldDirty: true });

      const endMonthYear = ascProject.predictedCapitalizationEndDate
        ? dateToMonthYear(ascProject.predictedCapitalizationEndDate)
        : null;
      setValue('capitalizationEndMonthYear', endMonthYear, { shouldDirty: true });
    }
  };

  const onSubmit: SubmitHandler<CapitalizationEditForm> = async (values) => {
    const capitalizationStartDate =
      values.isCapitalized && values.capitalizationStartMonthYear
        ? toIsoDateString(getFirstDateOfMonthYear(values.capitalizationStartMonthYear))
        : null;
    const capitalizationEndDate =
      values.isCapitalized && values.capitalizationEndMonthYear
        ? toIsoDateString(getLastDateOfMonthYear(values.capitalizationEndMonthYear))
        : null;

    await updateCapitalization({
      capitalizationStartDate,
      capitalizationEndDate,
    });

    onClose();
  };

  return (
    <Modal isOpen={isOpen}>
      <Stack as="form" direction="column" gap={4} onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          icon={<Presentation width={24} color={colors.blue[600]} />}
          iconBgColor={colors.blue[50]}
          title={
            <Typography fontSize="xl" fontWeight="medium">
              Edit Capitalization
            </Typography>
          }
        />

        <Stack direction="column">
          <Stack direction="column" gap={2}>
            <Stack direction="row" gap={3} alignItems="center">
              <Typography as="label" fontSize="sm" fontWeight="medium" color={colors.gray[700]}>
                Capitalized
              </Typography>
              <ControlledToggleSwitch
                control={control}
                name="isCapitalized"
                id={capitalizationStatusSwitchId}
                onChange={handleToggleChange}
                withIcon
                disabled={fieldConfig.status.disabled}
              />
            </Stack>
            {fieldConfig.status.helpText && (
              <Typography fontSize="xs" color={colors.gray[500]}>
                {fieldConfig.status.helpText}
              </Typography>
            )}
          </Stack>

          {isCapitalized && (
            <>
              <StyledDivider />
              <ControlledMonthYearPicker
                control={control}
                name="capitalizationStartMonthYear"
                id={capitalizationStartDateId}
                label="Milestone 1 - Capitalization Start"
                helpText={fieldConfig.startDate.helpText}
                monthOptions={defaultMonthOptions}
                min={fieldConfig.startDate.min}
                max={fieldConfig.startDate.max}
                disabled={fieldConfig.startDate.disabled}
                clearable={fieldConfig.startDate.clearable}
                rules={{ validate: fieldConfig.startDate.validate }}
              />
              <ControlledMonthYearPicker
                control={control}
                name="capitalizationEndMonthYear"
                id={capitalizationEndDateId}
                label={
                  <AscProjectDetailCapitalizationEndDateLabel
                    value={capitalizationEndMonthYear}
                    predictedValue={predictedCapitalizationEndMonthYear}
                    label="Milestone 2 - Capitalization End"
                  />
                }
                helpText={fieldConfig.endDate.helpText}
                monthOptions={defaultMonthOptions}
                min={fieldConfig.endDate.min}
                disabled={fieldConfig.endDate.disabled}
                clearable={fieldConfig.endDate.clearable}
                rules={{ validate: fieldConfig.endDate.validate }}
              />
            </>
          )}

          {approvalWarningMessage && isValid && (
            <StyledAlert
              severity="warning"
              title={approvalWarningMessage}
              titleSize="xs"
              $withTopMargin={!isCapitalized}
            />
          )}
        </Stack>

        <StyledModalActions hasTopBorder={false}>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            rounded="regular"
            size="base"
            type="submit"
            disabled={loading || !isDirty || !isValid}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </StyledModalActions>
      </Stack>
    </Modal>
  );
};
